import React, { ReactNode } from "react";
import {
  Badge,
  BadgeOwnProps,
  FormControl,
  MenuItem,
  SelectChangeEvent,
  SelectProps,
  Stack,
  Typography,
} from "@mui/material";
import MuiSelect from "@mui/material/Select";
import { KeyboardArrowDown } from "@mui/icons-material";
import { FieldError } from "react-hook-form";

export interface ISelectItem {
  value: string;
  label: string;
  badgeColor?: BadgeOwnProps["color"];
}

export type SelectCustomProps<TSelectItem extends object = ISelectItem> = {
  items: TSelectItem[];
  fullWidth?: boolean;
  labelName?: keyof TSelectItem;
  valueName?: keyof TSelectItem;
  renderValue?: (value: TSelectItem) => React.ReactNode;
  onChange?: (event: SelectChangeEvent<TSelectItem>, child: React.ReactNode) => void;
  textColor?: "primary";
  onItemClick?: (item: TSelectItem) => void;
  error?: FieldError;
  value?: TSelectItem[keyof TSelectItem];
} & Omit<SelectProps, "onChange" | "error" | "value">;

const Select = <TSelectItem extends Record<string, any> = ISelectItem>({
  items,
  value,
  onChange,
  sx,
  labelName = "label",
  valueName = "value",
  readOnly,
  label,
  onItemClick,
  error,
  placeholder,
  ...props
}: SelectCustomProps<TSelectItem>) => {
  const handleChange = (event: SelectChangeEvent<unknown>, child: ReactNode) => {
    const typedEvent = event as SelectChangeEvent<TSelectItem>;
    onChange?.(typedEvent, child);
  };
  const isCurrentItemWithBadge = Boolean(
    items.find((item) => (item[valueName || "value"] as string) === value)?.badgeColor as BadgeOwnProps["color"]
  );

  return (
    <FormControl
      fullWidth={props.fullWidth}
      component={Stack}
      spacing={0.5}
      error={!!error}>
      {label && (
        <Typography
          variant='body14rg'
          sx={{ color: "base.500" }}>
          {label}
        </Typography>
      )}
      <MuiSelect
        value={value || null}
        size='small'
        IconComponent={KeyboardArrowDown}
        onChange={handleChange}
        sx={sx}
        displayEmpty
        renderValue={
          !value && placeholder
            ? () => {
                return <Typography color='text.disabled'>{placeholder}</Typography>;
              }
            : undefined
        }
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          transformOrigin: {
            vertical: 0,
            horizontal: "center",
          },
        }}
        inputProps={
          isCurrentItemWithBadge
            ? {
                sx: { paddingLeft: 4 },
              }
            : {}
        }
        {...props}>
        {items.map((item) => {
          const itemLabel = item[labelName || "label"];
          const itemValue = item[valueName || "value"];
          const withBadge = item.badgeColor;

          return (
            <MenuItem
              key={`select-item-${itemValue}`}
              onClick={() => onItemClick?.(item)}
              textColor={props.textColor}
              value={itemValue}
              sx={withBadge ? { paddingLeft: 4 } : {}}>
              {withBadge ? (
                <Badge
                  color={item.badgeColor}
                  anchorOrigin={{ horizontal: "left", vertical: "top" }}
                  variant='dot'
                  slotProps={{
                    badge: {
                      style: {
                        top: "50%",
                        left: -12,
                        minWidth: 12,
                        height: 12,
                        borderRadius: "100%",
                      },
                    },
                  }}>
                  {itemLabel}
                </Badge>
              ) : (
                itemLabel
              )}
            </MenuItem>
          );
        })}
      </MuiSelect>
    </FormControl>
  );
};

export default Select;
